import { format } from 'date-fns';
import { determineBrandFromURL, BRANDS } from '../helpers/brand';
import { pageTracking } from '../tracking/tracking';
import { Tracking } from '../helpers/enums';
import { trapFocus } from '../helpers/util';

export const reviews = async () => {
  const body = document.querySelector('body');
  const onPageReviews = document.querySelector('[js-reviews]');
  const googleReviewsBtn = document.querySelector('[js-google-reviews]');
  const openOverlay = document.querySelectorAll('[js-reviews-overlay-open]');
  const closeOverlay = document.querySelectorAll('[js-reviews-overlay-close]');
  const backdrop = document.querySelector('[js-reviews-backdrop]');
  const modal = document.querySelector('[js-reviews-modal]');
  const modalContentWrapper = document.querySelector('[js-reviews-overlay-content]');
  const brand = determineBrandFromURL();
  const language = document.querySelector('html')?.getAttribute('lang')?.toLowerCase();

  if (!onPageReviews || !window['reviewsVars']?.url) {
    return;
  }

  const reviewRatingTextToUse = ({ score }: { score: number }): string | null => {
    let text = window['reviewsVars']?.fair;
    if (score >= 4.5) {
      text = window['reviewsVars']?.excellent;
    } else if (score >= 4) {
      text = window['reviewsVars']?.veryGood;
    } else if (score >= 3.5) {
      text = window['reviewsVars']?.good;
    }

    function toOneDecimal(n: number) {
      return n % 1 === 0 ? n : n.toFixed(1);
    }

    return `${toOneDecimal(score).toString()} ${brand === BRANDS.STAYCITY ? '-' : ''} ${text}`;
  };

  const reviewTotalTextToUse = ({ reviews }: { reviews: number }): string | null =>
    `(${reviews} ${window['reviewsVars']?.reviews})`;

  /* || GET REVIEWS API */
  const reviewsApi = async (hotelCode: string, hasCmsReviews: boolean) => {
    const url = `${window['reviewsVars']?.url}?contenttype=Review&language=${language}&OWSCode=${hotelCode}`;

    try {
      const response = await fetch(url);
      const data = await response.json();
      return data;
    } catch (err) {
      /* Error, hide reviews sections */
      if (!hasCmsReviews) {
        document.querySelectorAll('.reviews-item').forEach((item) => {
          item.remove();
        });
        return;
      }
    }
  };

  const convertCMSReviewsToJson = (reviewsList: NodeListOf<HTMLElement>) => {
    const convertedData: {
      author_name: string;
      Text: string;
      relative_time_description: string;
      Rating: number;
    }[] = [];
    if (reviewsList.length > 0) {
      reviewsList.forEach((review) => {
        const dateValues = review.getAttribute('js-reviews-data-item-date') as string;

        const dateValuesSplit = () => {
          const timeRemoved = dateValues.split(/(\s+)/)[0];
          if (timeRemoved) {
            if (timeRemoved.includes('.')) {
              return timeRemoved.split('.');
            } else {
              return timeRemoved.split('/');
            }
          }
        };

        const dateValuesSplitData = dateValuesSplit();

        if (dateValuesSplitData && dateValuesSplitData.length === 3) {
          convertedData.push({
            author_name: review.getAttribute('js-reviews-data-item-name') ?? '',
            Text: review.getAttribute('js-reviews-data-item-comment') ?? '',
            relative_time_description: format(
              new Date(
                Number(dateValuesSplitData[2]),
                Number(dateValuesSplitData[1]) - 1,
                Number(dateValuesSplitData[0])
              ),
              'dd/MM/yyyy'
            ),
            Rating: Number(review.getAttribute('js-reviews-data-item-rating')) ?? 1
          });
        }
      });
    }
    return convertedData;
  };

  /* || PROPERTY PAGE REVIEWS */
  /* Needs to get reviews and display them in a carousel. 
  Because the data needs to come from an API, the HTML for each review 
  needs to be added dynamically */
  const propertyPageReviews = async () => {
    const cmsReviewsAsJson = convertCMSReviewsToJson(document.querySelectorAll('[js-reviews-data-item]'));
    const hotelCode = onPageReviews.getAttribute('js-hotel-code');
    const placeId = onPageReviews?.getAttribute('js-place-id');
    const averageRatingSpan = document.querySelectorAll('[js-reviews-average]');
    const totalReviewsSpan = document.querySelectorAll('[js-reviews-total]');
    const reviewsWrapper = onPageReviews.querySelector('.swiper-wrapper');

    if (!hotelCode || !reviewsWrapper) {
      return;
    }
    const reviews = await reviewsApi(hotelCode, !!cmsReviewsAsJson);

    const reviewsObj = reviews ? JSON.parse(reviews?.content) : null;

    const averageRating =
      reviews && reviewsObj?.Result?.Reviews?.length > 0
        ? reviewsObj?.Result?.Rating
        : (cmsReviewsAsJson || []).map((item) => item.Rating)?.reduce((a, b) => a + b, 0) / cmsReviewsAsJson.length;

    const totalRatings =
      reviews && reviewsObj?.Result?.Reviews?.length > 0
        ? reviewsObj?.Result?.user_ratings_total
        : cmsReviewsAsJson.length;

    const reviewsList = cmsReviewsAsJson.length > 0 ? cmsReviewsAsJson : reviewsObj?.Result?.Reviews;

    /* Less than 3 reviews, hide reviews section and anchor links */
    if (+totalRatings < 3 || (!reviewsList && totalRatings == 0)) {
      document.querySelectorAll('.reviews-item').forEach((item) => {
        item.remove();
      });
      return;
    }

    openReviewsOverlayHandler(reviewsList, placeId, hotelCode);

    /* Set average rating in header */
    if (averageRatingSpan) {
      for (let i = 0; i < averageRatingSpan.length; i++) {
        averageRatingSpan[i].textContent = reviewRatingTextToUse({
          score: averageRating
        });
      }
    }

    /* Set total number of reviews */
    if (totalReviewsSpan) {
      for (let i = 0; i < totalReviewsSpan.length; i++) {
        totalReviewsSpan[i].textContent = totalRatings;
      }
    }

    populateReviewHtml(reviewsList, reviewsWrapper, false);
  };

  /* || CITY PAGE REVIEWS */
  const cityPageReviews = async () => {
    const cityReviewsSections = document.querySelectorAll('[js-hotel-code]');

    if (!cityReviewsSections) {
      return;
    }

    for (const cityReviewsSection of cityReviewsSections) {
      const hotelCode = cityReviewsSection.getAttribute('js-hotel-code');
      const cmsReviewsAsJson = convertCMSReviewsToJson(
        document.querySelectorAll(`[js-reviews-data-item-hotel-code="${hotelCode}"]`)
      );

      const placeId = cityReviewsSection.getAttribute('js-place-id');
      const averageRatingSpan = cityReviewsSection.querySelector('[js-reviews-average]');
      const totalRatingSpan = cityReviewsSection.querySelector('[js-reviews-count]');

      if (!hotelCode || !averageRatingSpan || !totalRatingSpan) {
        return;
      }

      const reviews = await reviewsApi(hotelCode, !!cmsReviewsAsJson);

      /* No reviews, hide reviews section */
      if (!reviews && cmsReviewsAsJson.length === 0) {
        cityReviewsSection.remove();
      } else {
        const reviewsObj = reviews ? JSON.parse(reviews?.content) : null;

        const averageRating =
          reviews && reviewsObj?.Result?.Reviews?.length > 0
            ? reviewsObj?.Result?.Rating
            : cmsReviewsAsJson.map((item) => item.Rating).reduce((a, b) => a + b, 0) / cmsReviewsAsJson.length;

        const reviewsTotal =
          reviews && reviewsObj?.Result?.Reviews?.length > 0
            ? reviewsObj?.Result?.user_ratings_total
            : cmsReviewsAsJson.length;

        const reviewsList = cmsReviewsAsJson.length > 0 ? cmsReviewsAsJson : reviewsObj?.Result?.Reviews;

        if (Number(averageRating) == 0 || Number(reviewsTotal) == 0) {
          cityReviewsSection.remove();
        }

        averageRatingSpan.textContent = reviewRatingTextToUse({
          score: averageRating
        });

        totalRatingSpan.textContent = reviewTotalTextToUse({
          reviews: reviewsTotal
        });

        openReviewsOverlayHandler(reviewsList, placeId, hotelCode);
      }
    }
  };

  /* On property page */
  if (onPageReviews.hasAttribute('js-reviews-property')) {
    propertyPageReviews();
  }

  /* On City page */
  if (onPageReviews.hasAttribute('js-reviews-city')) {
    cityPageReviews();
  }

  /* || FULL REVIEWS OVERLAY */
  if (!backdrop || !modal || !body || !closeOverlay || !modalContentWrapper) {
    return;
  }

  /* TODO -- will need to handle hotel code for city page */
  const openReviewsOverlayHandler = (reviews, placeId, hotelCode) => {
    /* Set place id on CTA, if not set, hide CTA */
    if (!placeId) {
      googleReviewsBtn?.remove();
    }
    googleReviewsBtn?.setAttribute('href', `https://search.google.com/local/reviews?placeid=${placeId}`);

    /* there are multiple buttons to open the overlay, add event listener to all of them */
    openOverlay.forEach((item) => {
      if (item.getAttribute('js-hotel-code') === hotelCode) {
        item.addEventListener('click', () => {
          /* populate review content */
          populateReviewHtml(reviews, modalContentWrapper, true);

          item.setAttribute('aria-expanded', 'true');
          backdrop.classList.add('backdrop-active');
          modal.classList.add('reviews-modal--active');
          body.classList.add('overflow--hidden');

          pageTracking(Tracking.OPEN_REVIEWS_OVERLAY);

          setTimeout(() => {
            const button = modal.querySelectorAll('button')[0];
            if (button) {
              button.focus();
            }
          }, 2000);

          trapFocus(modal as HTMLElement);
        });
      }
    });
  };

  /* || CLOSE REVIEWS OVERLAY */
  const closeModal = () => {
    openOverlay.forEach((item) => {
      item.addEventListener('click', () => {
        item.setAttribute('aria-expanded', 'false');
      });
    });
    backdrop.classList.remove('backdrop-active');
    modal.classList.remove('reviews-modal--active');
    body.classList.remove('overflow--hidden');
  };

  backdrop.addEventListener('click', closeModal);
  closeOverlay.forEach((item) => {
    item.addEventListener('click', closeModal);
  });

  /* || POPULATE HTML OF REVIEWS BOTH ON PROPERTY PAGE AND IN OVERLAY */
  const populateReviewHtml = (reviewsList, reviewsWrapper: Element, isOverlay = false) => {
    /* Clear html before populating. Mainly needed for the overlay which can be opened and closed with different properties */
    reviewsWrapper.innerHTML = '';

    /* Loop through and display reviews in carousel */
    for (const review in reviewsList) {
      /* || RATINGS CONTAINER */
      const ratingContainer = document.createElement('div');
      ratingContainer.classList.add(
        'flex',
        'gap--sm',
        'align-items--center',
        isOverlay ? 'margin--reviews-overlay-rating' : 'margin-lg--b'
      );

      /* || SVG STARS */
      /* Container for svg stars */
      const starGroupContainer = document.createElement('div');
      starGroupContainer.classList.add('relative');

      /* Container for grey svg stars */
      const starContainer = document.createElement('div');
      starContainer.classList.add('flex', 'gap--xs', 'align-items--center', 'relative');

      /* Container for filled in svg stars */
      const filledStarContainer = document.createElement('div');
      filledStarContainer.classList.add(
        'overflow--hidden',
        'absolute',
        'top--50',
        'left--0',
        'flex',
        'gap--xs',
        'align-items--center',
        'relative'
      );

      /* Svg star */
      const starSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      starSvg.classList.add('icon--sm', 'fill--review-unfilled');

      /* Filled svg star */
      const filledStarSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      filledStarSvg.classList.add('icon--sm', 'fill--primary', 'flex-shrink--0');

      /* Svg star <use> tag */
      const starUse = document.createElementNS('http://www.w3.org/2000/svg', 'use');
      starUse.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', '/dist/assets/icons/sprite.svg#star');

      /* Append star <use> to both star svgs */
      starSvg?.append(starUse);
      filledStarSvg?.append(starUse.cloneNode());

      /* Clone star svg so that 5 can be appended */
      const starSvg2 = starSvg.cloneNode(true);
      const starSvg3 = starSvg.cloneNode(true);
      const starSvg4 = starSvg.cloneNode(true);
      const starSvg5 = starSvg.cloneNode(true);

      /* Clone filled star svg so that 5 can be appended */
      const filledStarSvg2 = filledStarSvg.cloneNode(true);
      const filledStarSvg3 = filledStarSvg.cloneNode(true);
      const filledStarSvg4 = filledStarSvg.cloneNode(true);
      const filledStarSvg5 = filledStarSvg.cloneNode(true);

      const rating = reviewsList[review].Rating;

      /* Append star svgs to star container */
      starContainer.append(starSvg);
      starContainer.append(starSvg2);
      starContainer.append(starSvg3);
      starContainer.append(starSvg4);
      starContainer.append(starSvg5);

      if (rating >= 1) {
        filledStarContainer.append(filledStarSvg);
      }
      if (rating >= 2) {
        filledStarContainer.append(filledStarSvg2);
      }
      if (rating >= 3) {
        filledStarContainer.append(filledStarSvg3);
      }
      if (rating >= 4) {
        filledStarContainer.append(filledStarSvg4);
      }
      if (rating == 5) {
        filledStarContainer.append(filledStarSvg5);
      }

      /* Append grey and filled stars into container */
      starGroupContainer.append(starContainer);
      starGroupContainer.append(filledStarContainer);

      /* || RATINGS TEXT */
      const ratingText = document.createElement('p');
      ratingText.classList.add('t-copy-small');
      ratingText.textContent = reviewsList[review].Rating + '/ 5';

      /* Append star container and rating text to rating container */
      ratingContainer.append(starGroupContainer);
      ratingContainer.append(ratingText);

      /* || REVIEW AUTHOR AND DATE */
      const author = document.createElement('p');
      author.classList.add(
        brand === BRANDS.WILDE ? 't-copy-small' : 't-copy-tiny',
        'opacity--7',
        isOverlay ? 'padding-0' : 'padding-md--t',
        isOverlay ? 'border--none' : 'border--top'
      );
      author.textContent = ', ' + reviewsList[review].relative_time_description;

      /* Author name. Make bold */
      const authorName = document.createElement(brand === BRANDS.WILDE || isOverlay ? 'span' : 'strong');
      authorName.textContent = reviewsList[review].author_name;

      /* Prepend so that it goes at start of text content */
      author.prepend(authorName);

      /* || BODY TEXT */
      const reviewText = document.createElement('p');
      reviewText.classList.add(
        brand === BRANDS.WILDE ? 't-copy-big' : 't-copy-small',
        brand === BRANDS.WILDE ? 'margin-md--t' : 'margin-xs--t',
        isOverlay ? 'border--bottom-not-last' : 'line-clamp',
        isOverlay ? 'padding--reviews-overlay-review' : 'line-clamp--5'
      );

      /* Add quotation marks around the quote if quote exists */
      if (reviewsList[review].Text) {
        reviewText.textContent = '"' + reviewsList[review].Text + '"';
      }

      /* Overlay is not a carousel so doesn't need to be appened into slide div */
      if (isOverlay) {
        reviewsWrapper.append(ratingContainer);
        reviewsWrapper.append(author);
        reviewsWrapper.append(reviewText);
      } else {
        /* || SWIPER SLIDE */
        const slide = document.createElement('div');
        slide.classList.add('swiper-slide');

        /* Append rating container to slide */
        slide.append(ratingContainer);
        slide.append(author);
        slide.append(reviewText);

        /* Append slide to wrapper */
        reviewsWrapper?.append(slide);
      }
    }
  };
};
